import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Settings2, Check } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getMyBilling } from "@/lib/billing.functions";
import { supabase } from "@/integrations/supabase/client";
import {
  MODULES, BASE_PLANS, computePrice,
  type BasePlanId, type BillingCycle,
} from "@/lib/billing/modules";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

export const Route = createFileRoute("/_authenticated/app/moduli")({ component: MyModulesPage });

function MyModulesPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null);
  const [plan, setPlan] = useState<BasePlanId>("pro");
  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const [mods, setMods] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const coLoading = false;

  const refresh = () => getMyBilling().then(setData).catch(() => setData(null));
  useEffect(() => {
    refresh();
    supabase.auth.getUser().then(({ data: { user } }) => user && setUser({ id: user.id, email: user.email }));
  }, []);

  useEffect(() => {
    if (!data) return;
    setPlan((data.plan as BasePlanId) ?? "starter");
    setCycle((data.billingCycle as BillingCycle) ?? "monthly");
    setMods((data.activeModules as string[]) ?? []);
  }, [data]);

  const breakdown = useMemo(() => computePrice(plan, mods, cycle), [plan, mods, cycle]);
  const currentBreakdown = useMemo(() => {
    if (!data) return null;
    return computePrice(
      (data.plan as BasePlanId) ?? "starter",
      (data.activeModules as string[]) ?? [],
      (data.billingCycle as BillingCycle) ?? "monthly",
    );
  }, [data]);

  if (!data) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const toggleModule = (id: string) =>
    setMods((p) => (p.includes(id) ? p.filter((m) => m !== id) : [...p, id]));

  const currentMods = ((data.activeModules as string[]) ?? []).slice().sort().join(",");
  const selMods = mods.slice().sort().join(",");
  const unchanged = plan === data.plan && currentMods === selMods && cycle === (data.billingCycle ?? "monthly");

  const buildCheckoutItems = () => {
    const planDef = BASE_PLANS.find((p) => p.id === plan);
    if (!planDef) return [];
    const items: { priceId: string; quantity?: number }[] = [];
    if (planDef.id !== "starter") {
      items.push({ priceId: cycle === "annual" ? `${planDef.id}_plan_annual` : `${planDef.id}_plan_monthly`, quantity: 1 });
    }
    const standardSel = breakdown.selectedModules.filter((m) => m.tier === "standard");
    const includedIds = new Set<string>();
    if (planDef.includedStandardModules === -1) {
      breakdown.selectedModules.forEach((m) => includedIds.add(m.id));
    } else if (planDef.includedStandardModules > 0) {
      standardSel.slice(0, planDef.includedStandardModules).forEach((m) => includedIds.add(m.id));
    }
    for (const m of breakdown.selectedModules) {
      if (includedIds.has(m.id)) continue;
      items.push({ priceId: cycle === "annual" ? m.priceIdAnnual : m.priceIdMonthly, quantity: 1 });
    }
    return items;
  };

  const handleConfirm = async () => {
    const items = buildCheckoutItems();
    if (!items.length) {
      toast.info(t("pages.moduli.noPaidItems"));
      setConfirmOpen(false);
      return;
    }
    setConfirmOpen(false);
    const names = items.map((i) => i.priceId).join(", ");
    openWhatsAppContact(`Ciao Gianluca, vorrei attivare questi moduli LexAI Pro: ${names}.`);
  };

  const cycleLabel = (c: BillingCycle) => c === "annual" ? t("pages.moduli.cycleAnnual") : t("pages.moduli.cycleMonthly");

  return (
    <>
      <PageHeader
        title={t("pages.moduli.title")}
        description={t("pages.moduli.description")}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/app/billing"><Settings2 className="mr-2 h-4 w-4" />{t("pages.moduli.goBilling")}</Link>
          </Button>
        }
      />
      <PageBody>
        {/* Stato attuale */}
        <Card className="mb-6 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm text-muted-foreground">{t("pages.moduli.currentConfig")}</div>
              <div className="font-display text-xl font-bold capitalize">
                {t("pages.moduli.summaryLine", {
                  plan: data.plan,
                  cycle: cycleLabel(data.billingCycle as BillingCycle),
                  n: (data.activeModules ?? []).length,
                })}
              </div>
            </div>
            {currentBreakdown && (
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{t("pages.moduli.currentCost")}</div>
                <div className="font-display text-2xl font-bold">€{currentBreakdown.total.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">{t("pages.moduli.perMonth")}</span></div>
              </div>
            )}
          </div>
        </Card>

        {/* Preview piano + cycle */}
        <h2 className="mb-3 font-display text-lg font-semibold">{t("pages.moduli.planAndCycle")}</h2>
        <div className="mb-4 flex items-center gap-2">
          <div className="inline-flex rounded-full border bg-muted/40 p-1">
            <button type="button" onClick={() => setCycle("monthly")}
              className={cn("rounded-full px-4 py-1.5 text-xs font-medium transition", cycle === "monthly" ? "bg-background shadow" : "text-muted-foreground")}>{t("pages.moduli.cycleMonthly")}</button>
            <button type="button" onClick={() => setCycle("annual")}
              className={cn("rounded-full px-4 py-1.5 text-xs font-medium transition", cycle === "annual" ? "bg-background shadow" : "text-muted-foreground")}>
              {t("pages.moduli.cycleAnnual")} <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-600">{t("pages.moduli.annualBadge")}</span>
            </button>
          </div>
        </div>
        <div className="mb-6 grid gap-3 lg:grid-cols-3">
          {BASE_PLANS.map((p) => {
            const sel = plan === p.id;
            const monthly = cycle === "annual" ? Math.round(p.monthlyPrice * 0.8) : p.monthlyPrice;
            return (
              <button key={p.id} type="button" onClick={() => setPlan(p.id)}
                className={cn("rounded-xl border p-4 text-left transition", sel ? "border-accent ring-2 ring-accent/40" : "hover:border-foreground/30")}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-base font-bold">{p.name}</h3>
                  {sel && <Check className="h-4 w-4 text-accent" />}
                </div>
                <div className="mt-1 font-display text-2xl font-bold">€{monthly}<span className="text-xs font-normal text-muted-foreground">{t("pages.moduli.perMonth")}</span></div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {p.includedStandardModules === -1
                    ? t("pages.moduli.allModulesIncluded")
                    : p.includedStandardModules > 0
                      ? t("pages.moduli.includedStandard", { n: p.includedStandardModules })
                      : t("pages.moduli.modulesApart")}
                </div>
              </button>
            );
          })}
        </div>

        {/* Toggle moduli */}
        <h2 className="mb-3 font-display text-lg font-semibold">{t("pages.moduli.specializations")}</h2>
        <p className="mb-3 text-sm text-muted-foreground">{t("pages.moduli.specializationsDesc")}</p>
        <div className="mb-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => {
            const on = mods.includes(m.id);
            return (
              <button key={m.id} type="button" onClick={() => toggleModule(m.id)}
                className={cn("flex items-start justify-between gap-3 rounded-lg border p-3 text-left transition",
                  on ? "border-accent bg-accent/5" : "hover:border-foreground/30")}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">{m.name}</span>
                    <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase">{m.tier}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.short}</div>
                </div>
                <div className="shrink-0 text-right">
                  <span className="text-sm font-semibold">€{m.monthlyPrice}<span className="text-[10px] font-normal text-muted-foreground">{t("pages.moduli.perMonth")}</span></span>
                  <div className={cn("mt-1 inline-block h-4 w-4 rounded border", on ? "bg-accent border-accent" : "border-foreground/30")}>
                    {on && <Check className="h-4 w-4 text-accent-foreground" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Riepilogo + CTA */}
        <Card className="mb-8 p-5">
          <div className="grid gap-4 md:grid-cols-[1fr,auto] md:items-center">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("pages.moduli.planLabel", { name: BASE_PLANS.find((p) => p.id === plan)?.name })}</span><span>€{breakdown.basePrice.toFixed(2)}</span></div>
              {breakdown.modulesPrice > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">{t("pages.moduli.modulesLabel", { n: breakdown.selectedModules.length })}</span><span>€{breakdown.modulesPrice.toFixed(2)}</span></div>
              )}
              {breakdown.includedDiscount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>{t("pages.moduli.includedDiscount")}</span><span>−€{breakdown.includedDiscount.toFixed(2)}</span></div>
              )}
              {breakdown.bundleDiscount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>{t("pages.moduli.bundleDiscount", { pct: breakdown.bundleDiscountPct })}</span><span>−€{breakdown.bundleDiscount.toFixed(2)}</span></div>
              )}
              {breakdown.cycleDiscount > 0 && (
                <div className="flex justify-between text-emerald-600"><span>{t("pages.moduli.cycleDiscount", { pct: breakdown.cycleDiscountPct })}</span><span>−€{breakdown.cycleDiscount.toFixed(2)}</span></div>
              )}
              <div className="mt-2 flex items-baseline justify-between border-t pt-2">
                <span className="font-display text-base font-semibold">{cycle === "annual" ? t("pages.moduli.totalAnnualBilled") : t("pages.moduli.totalMonthly")}</span>
                <span className="font-display text-2xl font-bold">€{breakdown.total.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">{t("pages.moduli.perMonth")}</span></span>
              </div>
              {cycle === "annual" && (
                <div className="text-right text-xs text-muted-foreground">{t("pages.moduli.perYear", { amount: breakdown.totalAnnualEquivalent.toFixed(2) })}</div>
              )}
            </div>
            <Button size="lg" className="md:min-w-[200px]" disabled={unchanged || coLoading} onClick={() => setConfirmOpen(true)}>
              {unchanged ? t("pages.moduli.currentSel") : t("pages.moduli.confirmPay")}
            </Button>
          </div>
        </Card>
      </PageBody>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pages.moduli.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>{t("pages.moduli.switchingTo")}</div>
                <ul className="list-disc pl-5">
                  <li>{t("pages.moduli.itemPlan", { plan: plan, cycle: cycleLabel(cycle).toLowerCase() })}</li>
                  <li>{t("pages.moduli.itemModules", { n: breakdown.selectedModules.length, names: breakdown.selectedModules.map((m) => m.name).join(", ") || t("pages.moduli.itemNone") })}</li>
                  <li>
                    {t("pages.moduli.itemTotal", { total: breakdown.total.toFixed(2) })}
                    {cycle === "annual" && t("pages.moduli.itemTotalAnnual", { annual: breakdown.totalAnnualEquivalent.toFixed(2) })}
                  </li>
                </ul>
                <div className="text-xs text-muted-foreground">{t("pages.moduli.checkoutHint")}</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("pages.moduli.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>{t("pages.moduli.proceed")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
